import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { showToast } from '@/components/ui/show-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { useForgotPasswordMutation } from '@/services/auth/queries';
import { usePostHog } from 'posthog-js/react';

const formSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' })
});

type UserFormValue = z.infer<typeof formSchema>;

export default function ForgotPasswordForm() {
  const posthog = usePostHog();
  const form = useForm<UserFormValue>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: ''
    }
  });

  const { mutate, isPending } = useForgotPasswordMutation(
    () => {
      posthog?.capture('form_submitted', { form_name: 'forgot_password', success: true });
      showToast('success', 'Password reset link sent to your email');
      form.reset();
    },
    (error) => {
      posthog?.capture('form_submitted', { form_name: 'forgot_password', success: false });
      if (error.response?.status === 404) {
        showToast('error', 'Email not found. Please check your email address.');
      } else {
        showToast('error', 'Failed to send reset link. Please try again.');
      }
    }
  );

  const onSubmit = (data: UserFormValue) => {
    mutate({ email: data.email });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className='w-full space-y-4'
      >
        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-sm font-bold'>Email address</FormLabel>
              <FormControl>
                <Input
                  type='email'
                  placeholder='Enter your email...'
                  disabled={isPending}
                  className='h-[50px]'
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button
          type='submit'
          disabled={isPending}
          className='h-12 w-full text-white'
          aria-label='Send reset link'
          trackingEvent='forgot_password_submitted'
          trackingProperties={{ location: 'forgot_password_page' }}
        >
          {isPending ? 'Sending...' : 'Send my password reset link'}
        </Button>
      </form>

      <div className='mx-auto w-full pt-5 text-center'>
        <Link
          to={'/sign-in'}
          className='w-full text-primary transition-colors hover:text-primary/80'
        >
          Go back to Login
        </Link>
      </div>
    </Form>
  );
}
